import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Package, Plus, Trash2, Edit, Store, Tag, BarChart3, AlertCircle, Search, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const InventoryPage = () => {
  const { user, authAxios, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showRule, setShowRule] = useState(false);
  const [form, setForm] = useState({ name: "", sku: "", description: "", price: "", stock: "", category: "", weight: "1", publish_to: [] });
  const [ruleForm, setRuleForm] = useState({ marketplace_id: "mercadolibre", rule_type: "percentage", value: "10", active: true });
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [pRes, sRes, rRes] = await Promise.all([
        authAxios.get("/inventory/products"),
        authAxios.get("/inventory/stats"),
        authAxios.get("/pricing-rules")
      ]);
      setProducts(pRes.data); setStats(sRes.data); setRules(rRes.data);
    } catch (e) { /* error handled */; } finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => { if (user) fetchData(); }, [user, fetchData]);

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      await authAxios.post("/inventory/products", { ...form, price: parseFloat(form.price), stock: parseInt(form.stock || "0"), weight: parseFloat(form.weight || "1") });
      toast.success("Producto creado"); setShowAdd(false); setForm({ name: "", sku: "", description: "", price: "", stock: "", category: "", weight: "1", publish_to: [] }); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al crear"); }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("¿Eliminar producto?")) return;
    try { await authAxios.delete(`/inventory/products/${id}`); toast.success("Eliminado"); fetchData(); } catch (e) { toast.error("Error"); }
  };

  const handleAddRule = async (e) => {
    e.preventDefault();
    try {
      await authAxios.post("/pricing-rules", { ...ruleForm, value: parseFloat(ruleForm.value) });
      toast.success("Regla creada"); setShowRule(false); fetchData();
    } catch (error) { toast.error("Error al crear regla"); }
  };

  const handleDeleteRule = async (id) => {
    try { await authAxios.delete(`/pricing-rules/${id}`); toast.success("Regla eliminada"); fetchData(); } catch (e) { toast.error("Error"); }
  };

  const togglePublish = (mk) => {
    setForm(prev => ({ ...prev, publish_to: prev.publish_to.includes(mk) ? prev.publish_to.filter(m => m !== mk) : [...prev.publish_to, mk] }));
  };

  const filtered = products.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.sku.toLowerCase().includes(search.toLowerCase()));

  if (authLoading) return <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6] flex items-center justify-center">Cargando...</div>;
  if (!user) return <Navigate to="/login" />;

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6]" data-testid="inventory-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div><h1 className="text-3xl font-bold text-[#5D4037]">Inventario Multicanal</h1><p className="text-stone-600">Gestiona tu stock unificado</p></div>
          <div className="flex gap-3">
            <button onClick={() => setShowRule(true)} className="btn-secondary flex items-center gap-2 text-sm" data-testid="add-rule-btn"><Tag size={16} /> Regla de precio</button>
            <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-2 text-sm" data-testid="add-product-btn"><Plus size={16} /> Nuevo Producto</button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Productos", value: stats.total_products, color: "#2E7D32" },
              { label: "Stock Total", value: stats.total_stock, color: "#3B82F6" },
              { label: "Valor Total", value: `$${stats.total_value?.toLocaleString("es-MX")}`, color: "#4CAF50" },
              { label: "Stock Bajo", value: stats.low_stock_count, color: stats.low_stock_count > 0 ? "#EF4444" : "#4CAF50" },
            ].map((s, i) => (
              <div key={i} className="bento-card p-4" data-testid={`inv-stat-${i}`}>
                <p className="text-2xl font-black text-[#5D4037]">{s.value}</p>
                <p className="text-xs text-stone-500">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Pricing Rules */}
        {rules.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-stone-500 mb-2">REGLAS DE PRECIOS</h3>
            <div className="flex flex-wrap gap-2">
              {rules.map((r) => (
                <div key={r.id} className="flex items-center gap-2 bg-white border border-stone-200 rounded-full px-3 py-1 text-sm">
                  <Tag size={14} className="text-[#F57C00]" />
                  <span>{r.marketplace_id}: {r.rule_type === "percentage" ? `+${r.value}%` : `+$${r.value}`}</span>
                  <button onClick={() => handleDeleteRule(r.id)} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre o SKU..." className="w-full h-12 pl-12 pr-4 rounded-xl border border-stone-200 outline-none" data-testid="inv-search" />
        </div>

        {/* Products */}
        {loading ? <div className="text-center py-12">Cargando...</div> : filtered.length === 0 ? (
          <div className="bento-card p-12 text-center"><Package size={48} className="text-stone-300 mx-auto mb-4" /><p className="text-stone-500">{products.length === 0 ? "Agrega tu primer producto" : "Sin resultados"}</p></div>
        ) : (
          <div className="grid gap-3">
            {filtered.map((p) => (
              <div key={p.id} className="bento-card p-4 flex items-center gap-4" data-testid={`product-${p.id}`}>
                <div className="w-12 h-12 bg-[#2E7D32]/10 rounded-xl flex items-center justify-center flex-shrink-0"><Package size={20} className="text-[#2E7D32]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><p className="font-bold text-[#5D4037]">{p.name}</p><span className="text-xs font-mono text-stone-400">{p.sku}</span></div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-sm font-bold text-[#2E7D32]">${p.price?.toLocaleString("es-MX")}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.stock <= 5 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>{p.stock} en stock</span>
                    {p.published_marketplaces?.map((mk, i) => (<span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{mk}</span>))}
                  </div>
                </div>
                <button onClick={() => handleDeleteProduct(p.id)} className="p-2 text-stone-400 hover:text-red-500"><Trash2 size={18} /></button>
              </div>
            ))}
          </div>
        )}

        {/* Add Product Modal */}
        {showAdd && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
              <h3 className="text-lg font-bold text-[#5D4037] mb-4">Nuevo Producto</h3>
              <form onSubmit={handleAddProduct} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-sm font-semibold text-[#5D4037]">Nombre *</label><input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-stone-200 outline-none text-sm" required data-testid="prod-name" /></div>
                  <div><label className="text-sm font-semibold text-[#5D4037]">SKU *</label><input type="text" value={form.sku} onChange={(e) => setForm({...form, sku: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-stone-200 outline-none text-sm font-mono" required data-testid="prod-sku" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="text-sm font-semibold text-[#5D4037]">Precio *</label><input type="number" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} step="0.01" className="w-full h-10 px-3 rounded-lg border border-stone-200 outline-none text-sm" required data-testid="prod-price" /></div>
                  <div><label className="text-sm font-semibold text-[#5D4037]">Stock</label><input type="number" value={form.stock} onChange={(e) => setForm({...form, stock: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-stone-200 outline-none text-sm" data-testid="prod-stock" /></div>
                  <div><label className="text-sm font-semibold text-[#5D4037]">Peso (kg)</label><input type="number" value={form.weight} onChange={(e) => setForm({...form, weight: e.target.value})} step="0.1" className="w-full h-10 px-3 rounded-lg border border-stone-200 outline-none text-sm" /></div>
                </div>
                <div><label className="text-sm font-semibold text-[#5D4037]">Publicar en:</label>
                  <div className="flex gap-2 mt-1">
                    {["mercadolibre", "amazon"].map((mk) => (
                      <button key={mk} type="button" onClick={() => togglePublish(mk)} className={`px-3 py-1 rounded-full text-sm border ${form.publish_to.includes(mk) ? "bg-[#2E7D32] text-white border-[#2E7D32]" : "border-stone-200 text-stone-600"}`}>
                        {mk === "mercadolibre" ? "Mercado Libre" : "Amazon"}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowAdd(false)} className="flex-1 h-10 border border-stone-200 rounded-lg font-medium text-sm">Cancelar</button>
                  <button type="submit" className="flex-1 btn-primary h-10 text-sm" data-testid="prod-submit">Crear Producto</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Rule Modal */}
        {showRule && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-bold text-[#5D4037] mb-4">Nueva Regla de Precio</h3>
              <form onSubmit={handleAddRule} className="space-y-3">
                <div><label className="text-sm font-semibold text-[#5D4037]">Marketplace</label><select value={ruleForm.marketplace_id} onChange={(e) => setRuleForm({...ruleForm, marketplace_id: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-stone-200 outline-none text-sm bg-white"><option value="mercadolibre">Mercado Libre</option><option value="amazon">Amazon</option></select></div>
                <div><label className="text-sm font-semibold text-[#5D4037]">Tipo</label><select value={ruleForm.rule_type} onChange={(e) => setRuleForm({...ruleForm, rule_type: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-stone-200 outline-none text-sm bg-white"><option value="percentage">Porcentaje (+%)</option><option value="fixed">Fijo (+$)</option></select></div>
                <div><label className="text-sm font-semibold text-[#5D4037]">Valor</label><input type="number" value={ruleForm.value} onChange={(e) => setRuleForm({...ruleForm, value: e.target.value})} step="0.01" className="w-full h-10 px-3 rounded-lg border border-stone-200 outline-none text-sm" /></div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowRule(false)} className="flex-1 h-10 border border-stone-200 rounded-lg font-medium text-sm">Cancelar</button>
                  <button type="submit" className="flex-1 btn-primary h-10 text-sm" data-testid="rule-submit">Crear Regla</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryPage;
